import React from "react";
import PropTypes from "prop-types"; // PropTypes for type checking
import { Box, Grid, TextField } from "@material-ui/core"; // Material-UI components for layout and input
import Autocomplete from "@material-ui/lab/Autocomplete"; // Autocomplete component for suggestions
import parse from "autosuggest-highlight/parse"; // Utility to parse highlighted text
import match from "autosuggest-highlight/match"; // Utility to match highlighted text
import { makeStyles } from "@material-ui/core/styles"; // Material-UI styling utility
import styles from "assets/jss/material-dashboard-react/components/businessForm"; // Custom styles for the component
import { getEmailData } from "Redux/Actions/merchantInformation"; // Redux action to fetch email data
import { useDispatch } from "react-redux"; // Redux dispatch hook
import { profileCodePermissionCheck } from "../../Utils/commonUtils"; // Utility for profile code permission checks

// Custom styles for the component
const useStyles = makeStyles(styles);

const EmailAutofill = ({
  email, // Current email value
  onEmailChange, // Function to handle email input changes
  agentsList, // List of agents for autocomplete suggestions
  setSelectedUser, // Function to set the selected user
  allowManualInput, // Boolean to allow manual input
  emailError, // Boolean to indicate email validation error
  values, // Additional form values
}) => {
  const menu = JSON.parse(localStorage.getItem("menu")); // Retrieve menu data from localStorage
  const profileCode = menu?.Role_Permission_Data?.Profile_Code; // Extract profile code from menu
  const classes = useStyles(); // Apply custom styles
  const dispatch = useDispatch(); // Initialize Redux dispatch

  // Handle input change for the email field
  const handleInputChange = (event) => {
    const { value } = event.target;
    onEmailChange(value.toLowerCase()); // Convert email to lowercase
    setSelectedUser(null); // Reset selected user
  };

  return (
    <Box className={classes.root}>
      <Grid container>
        <Grid item lg={12} xs={12}>
          {/* Autocomplete component for email suggestions */}
          <Autocomplete
            disabled={!!(
                profileCode === profileCodePermissionCheck.profileCode.SA ||
                (values.Status_Code === 1 && values.Status_Code !== 3)
              ) ||
              (values.Status_Code !== 1 && values.Status_Code !== "") // Disable input based on profile code and status
            }
            name="agentId"
            id="agentId"
            style={{ fontSize: "14px" }} // Custom font size
            value={email} // Current email value
            autoHighlight // Highlight matching text in suggestions
            fullWidth // Full width input
            onInputChange={(e, newInputValue) => {
              onEmailChange(newInputValue.toLowerCase()); // Update email value
              setSelectedUser(null); // Reset selected user
            }}
            onChange={(_, value) => {
              setSelectedUser(value); // Set selected user
              if (value) {
                dispatch(getEmailData(value)); // Fetch email data for the selected user
              } else {
                onEmailChange(""); // Reset email value
                setSelectedUser(null); // Reset selected user
              }
            }}
            options={agentsList} // List of suggestions
            getOptionLabel={(option) => option} // Display option as is
            renderInput={(params) => (
              <TextField
                {...params}
                label="Email" // Input label
                value={email || ""} // Current email value
                onChange={handleInputChange} // Handle input change
                required // Mark input as required
                error={emailError} // Indicate validation error
                helperText={emailError ? "Invalid email" : ""} // Display error message
                disabled={
                  profileCode === profileCodePermissionCheck.profileCode.SA // Disable input for specific profile codes
                }
              />
            )}
            renderOption={(option, { inputValue }) => {
              const matches = match(option, inputValue); // Match input value with option
              const parts = parse(option, matches); // Parse matched parts for highlighting
              return (
                <div>
                  {parts.map((part, index) => (
                    <span
                      key={index}
                      style={{
                        backgroundColor: part.highlight ? "#f3ff0070" : "", // Highlight matched text
                        fontWeight: part.highlight ? 400 : "", // Adjust font weight for highlighted text
                        fontSize: "14px", // Custom font size
                      }}
                    >
                      {part.text}
                    </span>
                  ))}
                </div>
              );
            }}
            freeSolo={allowManualInput} // Allow manual input if specified
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// Define prop types for the component
EmailAutofill.propTypes = {
  email: PropTypes.string.isRequired, // Current email value
  onEmailChange: PropTypes.func.isRequired, // Function to handle email input changes
  agentsList: PropTypes.array.isRequired, // List of agents for autocomplete suggestions
  selectedUser: PropTypes.any, // Currently selected user
  setSelectedUser: PropTypes.func.isRequired, // Function to set the selected user
  allowManualInput: PropTypes.bool, // Boolean to allow manual input
  getEmailData: PropTypes.func, // Redux action to fetch email data
  emailError: PropTypes.bool, // Boolean to indicate email validation error
  setEmailError: PropTypes.func.isRequired, // Function to set email validation error
  values: PropTypes.object.isRequired, // Additional form values
};

// Export the component
export default EmailAutofill;
